// Import the necessary components
import VerofreshRecord from './VerofreshRecord'
import VerofreshHttpAdapter from '@/orm/VeroFreshHttpAdapter'
import { each, partition, find, sortBy, filter, some } from 'lodash'
import { $orm } from '@/orm'

class CustomerRecord extends VerofreshRecord {
  static get icon () {
    return 'building'
  }

  /**
   * Utility function to order a list of customers hierarchically, so that children fall under their parents. This is
   * useful in a couple cases when we're listing out customers in customer pickers, etc.
   * @param customers
   */
  static sortCustomersHierarchically (customers) {
    const results = []
    // partition the customers into parent and children
    let [parents, children] = partition(customers, c => c.isTopLevel())
    parents = sortBy(parents, 'name')
    children = sortBy(children, 'name')

    // Identify any child customers whose parents are not in the parents list, we will place these first.
    // We do this for users who have only child customers in the list - it basically acts as a normal sort in that case.
    each(children, c => {
      if (!some(parents, p => p.id === c.parent_id)) {
        results.push(c)
      }
    })

    // Now iterate through each parent, listing out their children along with them as we go. This will list everything
    // hierarchically.
    each(parents, p => {
      results.push(p)
      const ownChildren = filter(children, c => c.parent_id === p.id)
      each(ownChildren, c => {
        results.push(c)
      })
    })
    return results
  }

  timezone () {
    return this.getSetting('tz') || 'UTC'
  }

  tempFormat () {
    return this.getSetting('tempFormat') || 'C'
  }

  temperatureIcon () {
    switch (this.tempFormat()) {
      case 'F':
        return 'mdi-temperature-fahrenheit'
      default:
        return 'mdi-temperature-celsius'
    }
  }

  /**
   * Get whether this is a top-level (IE, company level) customer
   * @returns {boolean}
   */
  isTopLevel () {
    return this.parent_id === null
  }

  getSetting (settingKey, defaultValue = null) {
    if (this.customer_settings === undefined) {
      return defaultValue
    }

    const setting = this.customer_settings.filter(setting =>
      setting.key === settingKey
    )

    if (setting.length === 0) {
      return defaultValue
    }

    return setting[0].value
  }

  setSetting (settingKey, value) {
    if (this.customer_settings === undefined) {
      this.customer_settings = []
    }

    const setting = find(this.customer_settings, { key: settingKey })

    if (setting !== undefined) {
      setting.value = value
    } else {
      this.customer_settings.push({ key: settingKey, value: value })
    }
  }

  /**
   * Update the customer's settings
   * @param settings A JSON object containing the settings to update/create
   * @return {AxiosPromise}
   */
  updateSettings (settings) {
    return VerofreshHttpAdapter({
      url: $orm('Customer').endpoint + '/' + this.id + '/update-settings',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: JSON.stringify(settings)
    })
  }
}

const relations = {
  hasMany: {
    CustomerAssessmentTerm: {
      foreignKey: 'customer_id',
      localField: 'assessment_terms'
    }
  }
}

export default CustomerRecord
export { relations }
